import { useState } from 'react';
import type { ThemeModeContextValue } from 'src/components/context/ThemeModeContext';

type ThemeMode = ThemeModeContextValue['mode'];

/** Dark unless the `theme_mode` cookie says light. */
export const readThemeModeCookie = (): ThemeMode => {
  const match = document.cookie.match(/(?:^|;\s*)theme_mode=(dark|light)/);
  return match?.[1] === 'light' ? 'light' : 'dark';
};

/** better-ui: kill color/bg transitions during theme flip so the swap snaps instead of smearing */
const suppressThemeTransitions = () => {
  const style = document.createElement('style');
  style.setAttribute('data-theme-transition-suppress', 'true');
  style.appendChild(
    document.createTextNode('*,*::before,*::after{transition:none !important}')
  );
  document.head.appendChild(style);
  // Force a reflow so new theme colors commit while overrides still apply
  void document.body.offsetHeight;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      style.remove();
    });
  });
};

/** State behind ThemeModeContext: mode plus a toggle that persists to the `theme_mode` cookie. */
export const useThemeModeState = (): ThemeModeContextValue => {
  const [mode, setMode] = useState<ThemeMode>(readThemeModeCookie);

  const toggleMode = () => {
    suppressThemeTransitions();
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 365);
      document.cookie = `theme_mode=${next}; expires=${expiresAt.toUTCString()}; path=/; SameSite=Lax`;
      return next;
    });
  };

  return { mode, toggleMode };
};
